/* eslint-disable react-hooks/exhaustive-deps */
import {
  useAlert,
  useMeaningValueTestResult,
  useMyResulatOfTest,
  useNeedsProTestResult,
  useNeedsTestResult,
  usePreferencePersonalityTestResult,
  useRedirectToRootPage,
  useResultsClient,
  useStudyTestResult,
} from '@orientaction/hooks';
import { appLanguage } from '@orientaction/reducers';
import { MajorTest } from '@orientaction/services';
import { getFromLS, isAdmin } from '@orientaction/utils';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getMajorTestTemplateDescription } from '../../../shared/majorTestDescriptions';
import { getMajorTestConfig, getPreferenceShopFields } from '../config/majorTestConfigs';
import { getMajorResultBackPath } from '../majorResultBackPath';
import { MajorTestKind, UseMajorTestResultPageReturn } from '../types';
import useOnDemandPdf from './useOnDemandPdf';

const buildHistoryItems = (list: any[] = [], withPreferenceMarkers = false) =>
  list.map(item => ({
    id: item.id,
    createdAt: item.createdAt || item.created_at,
    ...(withPreferenceMarkers
      ? {
          updatedDate: item.updatedDate,
          consultantId: item.consultant_id?.id,
          realizationDate: item.realization_date,
        }
      : {}),
  }));

const useMajorTestResultPage = (kind: MajorTestKind): UseMajorTestResultPageReturn => {
  const staticConfig = getMajorTestConfig(kind);
  const { id: routeId }: { id?: string } = useParams();
  const resultId = Number(routeId);
  const lang: string = useSelector(appLanguage);

  const alert = useAlert();
  const { goToRootPage } = useRedirectToRootPage();
  const { myResult, setMyResult } = useMyResulatOfTest();

  const { getMyResult: getMeaningResult } = useMeaningValueTestResult();
  const { getMyResult: getNeedsResult } = useNeedsTestResult();
  const { getMyResult: getNeedsProResult } = useNeedsProTestResult();
  const { getMyResult: getPreferenceResult, getMyResultJeune } =
    usePreferencePersonalityTestResult();
  const { getMyResult: getStudyResult } = useStudyTestResult();
  // useResultsClient fetches /api/results/:id (Harmony Soft Skills).
  // Enable it only for harmony — other kinds use dedicated get-result-* APIs.
  const isHarmony = kind === 'harmony';
  const {
    results,
    resultToShow,
    loading: harmonyLoading,
    company_user_role,
  } = useResultsClient('email@test.com', String(resultId), isHarmony);

  const [loading, setLoading] = useState(true);
  const [openContactForm, setOpenContactForm] = useState(false);
  const [harmonyResult, setHarmonyResult] = useState<any>(null);
  const [harmonyHistory, setHarmonyHistory] = useState<any[]>([]);
  const [templateDescription, setTemplateDescription] = useState(
    getMajorTestTemplateDescription(staticConfig.contactFormTest.id)
  );

  const config = useMemo(() => {
    const goFurther =
      kind === 'preference'
        ? {
            ...staticConfig.goFurther,
            ...getPreferenceShopFields(myResult?.resultat, lang),
          }
        : staticConfig.goFurther;

    return {
      ...staticConfig,
      description: templateDescription,
      goFurther,
    };
  }, [staticConfig, templateDescription, kind, myResult, lang]);

  const handlePolicyError = (err: any) => {
    if (
      err?.response?.data?.error?.name === 'PolicyError' &&
      err?.response?.data?.error?.status === 403
    ) {
      alert.openSimpleAlert('Cette page ne vous est pas accessible.');
      goToRootPage();
      return true;
    }
    alert.openSimpleAlert();
    return false;
  };

  const fetchStandardResult = async (silent = false) => {
    if (!silent) {
      setLoading(true);
      window.scrollTo(0, 0);
    }

    try {
      let response;

      switch (kind) {
        case 'meaning':
          response = await getMeaningResult(resultId);
          break;
        case 'needs':
          response = await getNeedsResult(resultId);
          break;
        case 'needsPro':
          response = await getNeedsProResult(resultId);
          break;
        case 'preference':
          response = await getPreferenceResult(resultId);
          break;
        case 'preferenceJeune':
          response = await getMyResultJeune(resultId);
          break;
        case 'study':
          response = await getStudyResult(resultId);
          break;
        default:
          break;
      }

      if (response?.data?.data) {
        setMyResult(response.data.data);
      }
    } catch (err) {
      handlePolicyError(err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    const testId = staticConfig.contactFormTest.id;

    setTemplateDescription(getMajorTestTemplateDescription(testId));

    const fetchTemplateDescription = async () => {
      try {
        const token = getFromLS('userToken') || '';
        const userInfos = JSON.parse(getFromLS('userInfos') || '{}');
        const companyId = userInfos?.company?.id;

        if (!companyId || !token) {
          return;
        }

        const response = await MajorTest.getMyListMajorTest(companyId, token, '');
        const tests = response?.data?.list || [];
        const template = tests.find((test: { id: number | string }) => +test.id === testId);

        if (isMounted) {
          setTemplateDescription(getMajorTestTemplateDescription(testId, template?.description));
        }
      } catch {
        // Keep the shared fallback description already set in state.
      }
    };

    fetchTemplateDescription();

    return () => {
      isMounted = false;
    };
  }, [kind, staticConfig.contactFormTest.id]);

  useEffect(() => {
    if (isHarmony) {
      window.scrollTo(0, 0);
      // Auto-fetch is already handled by useResultsClient when enabled.
      return;
    }

    if (!Number.isNaN(resultId)) {
      fetchStandardResult();
    }
  }, [kind, resultId, isHarmony]);

  useEffect(() => {
    if (!isHarmony) {
      return;
    }

    setHarmonyResult(resultToShow);
    setHarmonyHistory(results);
    setLoading(harmonyLoading);
  }, [isHarmony, resultToShow, results, harmonyLoading]);

  const resolvedResult = kind === 'harmony' ? harmonyResult : myResult;
  const { downloadPdf, preparingPdf, pdfElement } = useOnDemandPdf(kind, resultId, resolvedResult);
  const historyItems =
    kind === 'harmony'
      ? buildHistoryItems(harmonyHistory)
      : buildHistoryItems(myResult?.listHistory || [], kind === 'preference');

  const toggleContactForm = () => {
    setOpenContactForm(prev => !prev);
  };

  const handleDownloadPdf = async () => {
    try {
      await downloadPdf();
    } catch {
      alert.openSimpleAlert('Impossible de préparer le PDF. Veuillez réessayer.');
    }
  };

  const beneficiaryUserId =
    kind === 'harmony' ? company_user_role : resolvedResult?.users_permissions_user;
  const staffView = isAdmin(JSON.parse(getFromLS('userInfos') || '{}'));

  return {
    loading,
    myResult: resolvedResult,
    resultId,
    backPath: getMajorResultBackPath(staffView, beneficiaryUserId),
    historyItems,
    openContactForm,
    toggleContactForm,
    handleDownloadPdf,
    preparingPdf,
    refreshResult: () => fetchStandardResult(true),
    pdfElement,
    config,
    resultUserId:
      resolvedResult?.users_permissions_user?.id ||
      resolvedResult?.users_permissions_user ||
      resolvedResult?.user?.id ||
      resolvedResult?.user ||
      null,
  };
};

export default useMajorTestResultPage;
